import React from 'react';
import { Tag, Tooltip } from 'antd';
import { NonStdLabelExtra } from '../../types/nonstandardOps';

/** 容器标签按"标识组"分组显示的公共工具：标签清单、确认完成的容器核对、终审页容器卡片、快照都用它 */
export const UNGROUPED_TEXT = '未分组';

type Grouped = NonStdLabelExtra;

export interface LabelSection<T extends Grouped> {
  /** 组名；未分组的是空字符串 */
  name: string;
  title: string;
  rows: T[];
  consumedCount: number;
}

/** 和后端一致：去掉首尾空白、连续空白合并成一个空格，"LPN  扫描"和"LPN 扫描"是同一组 */
export const normGroup = (value?: string | null) => String(value || '').split(/\s+/).filter(Boolean).join(' ');

/** 标识组输入框的下拉过滤：输入的恰好是已有的组名（例如默认选中了上次的组）时列出全部，方便换组；否则按输入的文字筛选 */
export const groupFilterOption = (names: string[]) => (input: string, option?: { value?: any }) => {
  const text = input.trim().toLowerCase();
  if (!text || names.includes(normGroup(input))) return true;
  return String(option?.value || '').toLowerCase().includes(text);
};

export const hasGroups = (rows: Grouped[]) => rows.some(row => normGroup(row.group) !== '');

/** 分组标题：LPN扫描（12）· 已消耗 3；没有标识组的归到最后的"未分组" */
export const sectionTitle = (name: string, total: number, consumed: number) =>
  `${name || UNGROUPED_TEXT}（${total}）${consumed > 0 ? ` · 已消耗 ${consumed}` : ''}`;

/** 按组分段：组按第一次出现的顺序，"未分组"（如果有）放最后；行的顺序不变 */
export function sectionsOf<T extends Grouped>(rows: T[]): LabelSection<T>[] {
  const names: string[] = [];
  rows.forEach(row => {
    const name = normGroup(row.group);
    if (name && !names.includes(name)) names.push(name);
  });
  const all = hasGroups(rows) && rows.some(row => normGroup(row.group) === '') ? [...names, ''] : names;
  return all.map(name => {
    const part = rows.filter(row => normGroup(row.group) === name);
    const consumedCount = part.filter(row => !!row.consumed).length;
    return { name, title: sectionTitle(name, part.length, consumedCount), rows: part, consumedCount };
  });
}

const fmt = (value: number | string | null | undefined) => (value === null || value === undefined || value === '' ? '' : String(Number(value)));

/** 40×30×20 cm（缺哪一项就用 ? 占位）；一项都没填返回空字符串 */
export function sizeText(row: Grouped): string {
  const parts = [fmt(row.length_cm), fmt(row.width_cm), fmt(row.height_cm)];
  if (parts.every(part => part === '')) return '';
  return `${parts.map(part => part || '?').join('×')} cm`;
}

export const weightText = (row: Grouped): string => (fmt(row.weight_g) ? `${fmt(row.weight_g)} g` : '');

export const hasSizeInfo = (rows: Grouped[]) => rows.some(row => sizeText(row) || weightText(row) || row.packaging_name || row.packaging_code);

/** 尺寸/重量（和所选包材）合在一格里显示；什么都没填就是 - */
export const sizeWeightCell = (row: Grouped, opts?: { oneLine?: boolean }) => {
  const size = sizeText(row);
  const weight = weightText(row);
  const packaging = row.packaging_name || row.packaging_code || '';
  if (!size && !weight && !packaging) return '-';
  const text = [size, weight].filter(Boolean).join(' · ');
  if (opts?.oneLine) {
    const full = [text, packaging].filter(Boolean).join('（') + (text && packaging ? '）' : '');
    return (
      <Tooltip title={<span style={{ whiteSpace: 'pre-wrap' }}>{[text, packaging && `包材：${packaging}`].filter(Boolean).join('\n')}</span>} placement="topLeft">
        <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{full}</span>
      </Tooltip>
    );
  }
  return (
    <div style={{ fontSize: 12 }}>
      {text ? <div>{text}</div> : null}
      {packaging ? <div style={{ color: '#8c8c8c' }}>包材：{packaging}</div> : null}
    </div>
  );
};

export const ConsumedTag: React.FC<{ title?: string }> = ({ title }) => (
  <Tooltip title={title || '容器里的东西已被二次加工，这个容器已经不存在了：不需要贴标签、记录位置，也不能再打印'}>
    <Tag style={{ color: '#8c8c8c', background: '#f0f0f0', borderColor: '#d9d9d9', marginInlineEnd: 0 }}>已消耗</Tag>
  </Tooltip>
);

/** 已消耗的行整行变灰 */
export const consumedRowStyle = (row: Grouped): React.CSSProperties | undefined => (row.consumed ? { color: '#8c8c8c', background: '#fafafa' } : undefined);

interface SectionsProps<T extends Grouped> {
  rows: T[];
  /** 画一张表；没有任何标识组时只调用一次（和以前的单表完全一样），有标识组时每个组调用一次 */
  renderTable: (rows: T[]) => React.ReactNode;
  /** 分组标题右侧的操作（整组打印、整组标记已消耗、修改标识组等） */
  renderExtra?: (section: LabelSection<T>) => React.ReactNode;
}

/** 按标识组分段显示：每段一个标题（组名（数量）· 已消耗 N）+ 一张表；没有标识组就是一张普通的表 */
export function LabelGroupSections<T extends Grouped>({ rows, renderTable, renderExtra }: SectionsProps<T>) {
  if (!hasGroups(rows)) return <>{renderTable(rows)}</>;
  return (
    <div>
      {sectionsOf(rows).map(section => (
        <div key={section.name || '__ungrouped__'} className="nonstd-label-section" style={{ marginBottom: 12 }}>
          <div
            className="nonstd-label-section-header"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
              padding: '6px 12px', background: '#f5f5f5', border: '1px solid #f0f0f0', borderBottom: 'none', borderRadius: '6px 6px 0 0',
            }}
          >
            <b className="nonstd-label-section-title">{section.title}</b>
            <span style={{ display: 'inline-flex', gap: 12, flexWrap: 'wrap' }}>{renderExtra ? renderExtra(section) : null}</span>
          </div>
          {renderTable(section.rows)}
        </div>
      ))}
    </div>
  );
}
